/**
 * Route protection, as a pure function.
 *
 * The decision is separated from the router so it can be tested without one, and so both halves of
 * the rule live in one place: an unauthenticated visitor to any app route lands on sign-in, and a
 * signed-in one does not see sign-in. Those are one rule, and splitting them across two files is
 * how one half quietly stops being true.
 */

import type { AuthStatus } from "./auth-context";

export const SIGN_IN_PATH = "/sign-in";
export const DEFAULT_SIGNED_IN_PATH = "/knowledge";

/**
 * Where a brand-new account lands.
 *
 * Signing in returns you to what you were doing; registering has no "what you were doing", and an
 * empty library is the least useful thing to hand somebody who has not connected an assistant
 * yet. The current design export makes the same choice — it sends a completed registration to
 * Install. A `?redirect=` that survived the registration still wins over this.
 */
export const REGISTERED_LANDING_PATH = "/install";

export type GuardDecision =
  | { kind: "allow" }
  | { kind: "redirect"; to: string; redirectParam?: string };

/**
 * Guard for a route that requires a session.
 *
 * `restoring` is not treated as signed out. The app does not mount its router until restoring has
 * finished, so this branch should be unreachable — it returns "allow" rather than bouncing a
 * signed-in person to sign-in if that assumption is ever broken by a future change.
 */
export function guardProtected(status: AuthStatus, pathname: string): GuardDecision {
  if (status === "anonymous") {
    return {
      kind: "redirect",
      to: SIGN_IN_PATH,
      // Where they were going, so signing in finishes the journey rather than dumping them on a
      // default screen. Only a path within this app is ever carried — see `safeRedirectTarget`.
      redirectParam: safeRedirectTarget(pathname),
    };
  }
  return { kind: "allow" };
}

/**
 * Guard for sign-in and register: a signed-in visitor must not see either.
 *
 * `landing` is where to go when there is no `?redirect=` to honour. Sign-in uses the default;
 * register passes `REGISTERED_LANDING_PATH`, because a new account has nothing to go back to.
 */
export function guardPublicOnly(
  status: AuthStatus,
  redirectParam?: string,
  landing: string = DEFAULT_SIGNED_IN_PATH,
): GuardDecision {
  if (status === "authenticated") {
    return { kind: "redirect", to: safeRedirectTarget(redirectParam) ?? landing };
  }
  return { kind: "allow" };
}

/**
 * A redirect target this app is willing to honour, or `undefined`.
 *
 * Anything that is not a single-slash-prefixed local path is dropped. `//evil.example` and
 * `https://evil.example` are both rejected: a redirect parameter that accepts an absolute URL is
 * an open redirect, and an open redirect on a sign-in page is a phishing primitive.
 */
export function safeRedirectTarget(value: string | undefined | null): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  if (!value.startsWith("/")) return undefined;
  if (value.startsWith("//")) return undefined;
  if (value.startsWith("/\\")) return undefined;
  if (value === SIGN_IN_PATH || value === "/register") return undefined;
  return value;
}
